import React, { useEffect, useRef, useState } from "react";
import { api, fmtDate } from "../../lib/api";
import { Avatar, PriorityBadge, PRIORITY, ConfirmModal } from "../common";
import { MessageBubble, FeedItem, AdItem } from "../MessageBubble";
import { ChevronDown, Send, Paperclip, Clock, Activity, Lock, Megaphone, Radio, Users, X, Trash2 } from "lucide-react";

const PRIORITIES = ["NORMAL", "INFO", "IMPORTANT", "ALERTE", "URGENT", "FLASH"];

export default function ChatPanel({ channel, messages, npcs, typing, onSent, onEdit, onDelete, onChannelDeleted, campaignId }) {
  const isFeed = channel && (channel.type === "info_descending" || channel.type === "info_interactive" || channel.type === "ad");
  const isAd = channel && channel.type === "ad";
  const npcParts = (channel?.participants_view || []).filter((p) => p.type === "npc");
  const npcOptions = npcParts.map((p) => npcs.find((n) => n.id === p.id)).filter(Boolean);

  const [speakAs, setSpeakAs] = useState(null);
  const [openSel, setOpenSel] = useState(false);
  const [text, setText] = useState("");
  const [priority, setPriority] = useState("NORMAL");
  const [schedule, setSchedule] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);
  const [scheduledAt, setScheduledAt] = useState("");
  const [file, setFile] = useState(null);
  const [sending, setSending] = useState(false);
  const streamRef = useRef(null);
  const fileRef = useRef(null);

  useEffect(() => {
    if (npcOptions.length && (!speakAs || !npcOptions.find((n) => n.id === speakAs.id))) {
      setSpeakAs(npcOptions[0]);
    }
    // eslint-disable-next-line
  }, [channel?.id, npcs.length]);

  useEffect(() => {
    if (streamRef.current) streamRef.current.scrollTop = streamRef.current.scrollHeight;
  }, [messages, typing]);

  async function send() {
    if ((!text.trim() && !file) || !speakAs) return;
    setSending(true);
    try {
      let attachment_id = null;
      let message_type = "text";
      if (file) {
        const fd = new FormData();
        fd.append("file", file);
        const up = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        attachment_id = up.data.id;
        message_type = (up.data.content_type || "").startsWith("image/") ? "image" : "file";
      }
      const payload = {
        content: text.trim(),
        message_type,
        sender_type: speakAs.type || "npc",
        sender_identity_id: speakAs.id,
        priority: isFeed ? priority : "NORMAL",
        attachment_id,
      };
      if (schedule && scheduledAt) payload.scheduled_at = new Date(scheduledAt).toISOString();
      const r = await api.post(`/channels/${channel.id}/messages`, payload);
      setText(""); setFile(null); setSchedule(false); setScheduledAt("");
      onSent?.(r.data, payload.scheduled_at ? "scheduled" : "sent");
    } finally {
      setSending(false);
    }
  }

  async function fakeTyping() {
    if (!speakAs) return;
    await api.post(`/channels/${channel.id}/typing`, { identity_id: speakAs.id, identity_name: speakAs.name, typing: true });
    setTimeout(() => api.post(`/channels/${channel.id}/typing`, { identity_id: speakAs.id, typing: false }).catch(() => {}), 4000);
  }

  if (!channel) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center text-center p-8">
        <Radio size={32} style={{ color: "var(--r-primary)", opacity: 0.4 }} />
        <div className="sys-label mt-4">SÉLECTIONNEZ UNE TRANSMISSION</div>
      </div>
    );
  }

  const TypeIcon = channel.type === "private" ? Lock : channel.type === "group" ? Users : (channel.type === "info_descending" || channel.type === "ad") ? Megaphone : Radio;

  return (
    <div className="flex-1 flex flex-col min-w-0 relative" style={{ background: "var(--r-void)" }}>
      <div className="h-14 px-4 flex items-center justify-between border-b border-[color:var(--r-primary)]/15 panel">
        <div className="flex items-center gap-3 min-w-0">
          <TypeIcon size={16} style={{ color: "var(--r-primary)" }} />
          <div className="min-w-0">
            <div className="font-semibold truncate">{channel.name}</div>
            <div className="sys-label">{channel.type === "info_descending" ? "CANAL DESCENDANT" : channel.type === "info_interactive" ? "FLUX INTERACTIF" : channel.type === "ad" ? "PUBLICITÉ" : channel.type === "group" ? "GROUPE" : "CANAL SÉCURISÉ"} · {(channel.participants_view || []).length} PARTICIPANTS</div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="sys-label flex items-center gap-1 blink"><span style={{ width: 6, height: 6, borderRadius: "50%", background: "var(--r-primary)" }} /> LIVE</span>
          <button data-testid="delete-channel-btn" title="Supprimer ce canal" onClick={() => setConfirmDel(true)} className="text-[color:var(--r-text-muted)] hover:text-[color:var(--r-alert)]"><Trash2 size={15} /></button>
        </div>
      </div>
      <ConfirmModal
        open={confirmDel}
        onClose={() => setConfirmDel(false)}
        onConfirm={async () => { await api.delete(`/channels/${channel.id}`); setConfirmDel(false); onChannelDeleted?.(); }}
        title="SUPPRIMER LE CANAL"
        confirmLabel="SUPPRIMER"
        message={`Supprimer le canal « ${channel.name} » et tous ses messages ? Cette action est irréversible.`}
      />

      <div ref={streamRef} className="flex-1 overflow-y-auto p-4 space-y-3">
        {messages.map((m) =>
          isAd ? (
            <AdItem key={m.id} msg={m} gmControls onDelete={onDelete} />
          ) : isFeed ? (
            <FeedItem key={m.id} msg={m} gmControls onDelete={onDelete} />
          ) : (
            <MessageBubble key={m.id} msg={m} gmControls onEdit={onEdit} onDelete={onDelete} />
          )
        )}
        {typing && (
          <div className="flex items-center gap-2 fade-up">
            <span className="sys-label" style={{ textTransform: "none", letterSpacing: "normal" }}>{typing} écrit</span>
            <span><span className="tdot" /><span className="tdot" /><span className="tdot" /></span>
          </div>
        )}
      </div>

      {/* composer */}
      <div className="border-t relative" style={{ borderColor: "color-mix(in srgb, var(--r-primary) 30%, transparent)" }}>
        <div className="absolute left-0 top-0 bottom-0 w-1" style={{ background: "var(--r-secondary)" }} />
        <div className="p-3 pl-4">
          <div className="flex items-center gap-2 flex-wrap mb-2">
            {/* SPEAK AS */}
            <div className="relative">
              <button
                data-testid="gm-identity-selector-trigger"
                onClick={() => setOpenSel((v) => !v)}
                className="flex items-center gap-2 px-2.5 py-1.5"
                style={{ border: "1px solid var(--r-secondary)", background: "color-mix(in srgb, var(--r-secondary) 10%, transparent)" }}
              >
                <span className="sys-label" style={{ color: "var(--r-secondary)" }}>PARLER EN TANT QUE</span>
                {speakAs && <Avatar src={speakAs.avatar} name={speakAs.name} size={22} />}
                <span className="font-semibold text-sm">{speakAs ? speakAs.name : "—"}</span>
                <ChevronDown size={14} />
              </button>
              {openSel && (
                <div className="absolute bottom-full mb-1 left-0 z-50 frame w-64 max-h-64 overflow-auto" style={{ background: "var(--r-panel)" }}>
                  {npcOptions.map((n) => (
                    <button
                      key={n.id}
                      data-testid={`gm-identity-option-${n.id}`}
                      onClick={() => { setSpeakAs(n); setOpenSel(false); }}
                      className="w-full flex items-center gap-2 px-3 py-2 hover:bg-[color:var(--r-primary)]/10 text-left"
                    >
                      <Avatar src={n.avatar} name={n.name} size={26} />
                      <div className="min-w-0">
                        <div className="text-sm font-medium truncate">{n.name}</div>
                        <div className="sys-label truncate">{n.function || n.type}</div>
                      </div>
                    </button>
                  ))}
                  {npcOptions.length === 0 && <div className="sys-label p-3">AUCUN PNJ DANS CE CANAL</div>}
                </div>
              )}
            </div>

            {isFeed && (
              <select data-testid="broadcast-priority-select" value={priority} onChange={(e) => setPriority(e.target.value)} className="input font-mono text-xs" style={{ width: "auto", padding: "6px 8px" }}>
                {PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            )}
            {isFeed && priority !== "NORMAL" && <PriorityBadge level={priority} />}

            <button data-testid="fake-typing-button" onClick={fakeTyping} className="btn btn-ghost text-[10px] flex items-center gap-1" title="Déclencher un faux indicateur de frappe">
              <Activity size={12} /> SIMULER FRAPPE
            </button>
            <button onClick={() => setSchedule((v) => !v)} className="btn btn-ghost text-[10px] flex items-center gap-1" style={{ color: schedule ? "var(--r-primary)" : undefined }}>
              <Clock size={12} /> PROGRAMMER
            </button>
          </div>

          {schedule && (
            <div className="mb-2 flex items-center gap-2">
              <input data-testid="schedule-datetime-input" type="datetime-local" className="input font-mono text-xs" style={{ width: "auto" }} value={scheduledAt} onChange={(e) => setScheduledAt(e.target.value)} />
              <button
                type="button"
                data-testid="schedule-now-button"
                onClick={() => {
                  const d = new Date();
                  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
                  setScheduledAt(d.toISOString().slice(0, 16));
                }}
                className="btn btn-ghost text-[10px] flex items-center gap-1"
                title="Mettre la date et l'heure actuelles"
              >
                <Clock size={11} /> MAINTENANT
              </button>
              <span className="sys-label">PUBLICATION AUTOMATIQUE</span>
            </div>
          )}

          {file && (
            <div className="mb-2 flex items-center gap-2 text-xs font-mono">
              <Paperclip size={12} style={{ color: "var(--r-primary)" }} /> {file.name}
              <button onClick={() => setFile(null)}><X size={12} /></button>
            </div>
          )}

          <div className="flex items-end gap-2">
            <input ref={fileRef} type="file" className="hidden" onChange={(e) => setFile(e.target.files[0] || null)} />
            <button data-testid="attach-button" onClick={() => fileRef.current?.click()} className="btn btn-ghost px-3 py-2.5"><Paperclip size={16} /></button>
            <textarea
              data-testid="chat-message-input"
              className="input resize-none font-sans"
              rows={1}
              placeholder={`Transmettre en tant que ${speakAs ? speakAs.name : "…"}`}
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
            />
            <button data-testid="chat-send-button" onClick={send} disabled={sending} className="btn btn-solid px-4 py-2.5 flex items-center gap-2">
              <Send size={15} /> {schedule ? "PLANIFIER" : "ENVOYER"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
